/**
 * UsersTab.js — User management panel (Phase 13)
 *
 * Shows a list of all users with role badges and active status.
 * Admin can create, edit, and deactivate users.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  UserPlus, ShieldCheck, Eye, Edit2, Trash2,
  Loader2, Search, ChevronLeft, ChevronRight, Shield,
  ShieldAlert, UserX,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { API } from '../../lib/api';
import { getCurrentUser, isSystemAdmin } from '../../lib/auth';

// ── Role badge ────────────────────────────────────────────────────────────────
function RoleBadge({ role }) {
  const configs = {
    system_admin:  { bg: '#fef3c7', color: '#92400e', icon: ShieldCheck, label: 'System Admin' },
    company_admin: { bg: '#fef9c3', color: '#713f12', icon: ShieldCheck, label: 'Company Admin' },
    project_admin: { bg: '#dbeafe', color: '#1e40af', icon: Shield,      label: 'Project Admin' },
    estimator:     { bg: '#f3f4f6', color: '#374151', icon: Eye,         label: 'Estimator' },
    // legacy
    admin:         { bg: '#fef3c7', color: '#92400e', icon: ShieldCheck, label: 'Admin (legacy)' },
  };
  const cfg = configs[role] || configs.estimator;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ── Active badge ──────────────────────────────────────────────────────────────
function ActiveBadge({ active }) {
  return (
    <span
      className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
      style={{
        background: active ? '#dcfce7' : '#fee2e2',
        color: active ? '#16a34a' : '#dc2626',
      }}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ── Form field ────────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide">
        {label}
      </Label>
      {children}
    </div>
  );
}

// ── User form dialog ──────────────────────────────────────────────────────────
function UserFormDialog({ open, onClose, onSaved, editingUser }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const currentUser = getCurrentUser();
  const isSysAdmin = isSystemAdmin();

  const [form, setForm] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'estimator',
  });

  useEffect(() => {
    if (open) {
      if (editingUser) {
        setForm({
          username: editingUser.username || '',
          password: '',
          email: editingUser.email || '',
          full_name: editingUser.full_name || '',
          role: editingUser.role || 'estimator',
        });
      } else {
        setForm({ username: '', password: '', email: '', full_name: '', role: 'estimator' });
      }
      setError('');
    }
  }, [open, editingUser]);

  function set(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!form.username.trim()) return setError('Username is required');
    if (!editingUser && !form.password) return setError('Password is required');
    if (form.password && form.password.length < 6) return setError('Password must be at least 6 characters');
    if (!form.full_name.trim()) return setError('Full name is required');

    setLoading(true);
    try {
      if (editingUser) {
        const payload = {
          email: form.email,
          full_name: form.full_name,
          role: form.role,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/users/${editingUser.id}`, payload);
      } else {
        await axios.post(`${API}/users`, {
          username: form.username.trim(),
          password: form.password,
          email: form.email,
          full_name: form.full_name.trim(),
          role: form.role,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save user';
      setError(Array.isArray(msg) ? msg.map(m => m.msg || m).join(', ') : msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#1a1a2e' }}>
            {editingUser ? `Edit User — ${editingUser.username}` : 'Create New User'}
          </DialogTitle>
          <DialogDescription>
            {editingUser
              ? 'Update the user details below. Leave password blank to keep the current password.'
              : 'Add a new team member. They will be able to log in with the credentials below.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          <Field label="Username">
            <Input
              value={form.username}
              onChange={e => set('username', e.target.value)}
              placeholder="e.g. jsmith"
              disabled={!!editingUser}
              className="!bg-white"
            />
          </Field>

          <Field label={editingUser ? 'New Password (leave blank to keep current)' : 'Password'}>
            <Input
              type="password"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              placeholder={editingUser ? '(unchanged)' : 'Min. 6 characters'}
              className="!bg-white"
            />
          </Field>

          <Field label="Full Name">
            <Input
              value={form.full_name}
              onChange={e => set('full_name', e.target.value)}
              placeholder="e.g. John Smith"
              className="!bg-white"
            />
          </Field>

          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="john@company.com.au"
              className="!bg-white"
            />
          </Field>

          <Field label="Role">
            <select
              value={form.role}
              onChange={e => set('role', e.target.value)}
              className="flex h-9 w-full rounded-md border border-[#e5e4df] bg-white px-3 py-2 text-sm"
              style={{ color: '#1a1a2e' }}
            >
              <option value="estimator">Estimator — Manage quotes, view-only everything else</option>
              <option value="project_admin">Project Admin — Full platform access, no user management</option>
              <option value="company_admin">Company Admin — Full access + manage company users</option>
              {isSysAdmin && (
                <option value="system_admin">System Admin — Cross-company full access</option>
              )}
            </select>
          </Field>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="!bg-[#1a1a2e] !text-white hover:!bg-[#2a2a4e]">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingUser ? 'Save Changes' : 'Create User')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Deactivate confirm dialog ─────────────────────────────────────────────────
function DeactivateConfirmDialog({ open, onClose, onConfirm, user }) {
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
      onClose();
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {user?.username}?</AlertDialogTitle>
          <AlertDialogDescription>
            This user will no longer be able to log in. Their data will be preserved.
            You can reactivate them later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className="!bg-red-600 hover:!bg-red-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deactivate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Pagination controls ────────────────────────────────────────────────────────
function Pagination({ skip, limit, total, onPrev, onNext }) {
  const page = Math.floor(skip / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  return (
    <div className="flex items-center justify-between mt-4">
      <span className="text-xs text-[#6b7280]">
        {total === 0 ? 'No users' : `${skip + 1}–${Math.min(skip + limit, total)} of ${total}`}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={skip === 0}
          className="!h-8 !text-xs"
        >
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <span className="text-xs text-[#6b7280]">Page {page} / {totalPages || 1}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={skip + limit >= total}
          className="!h-8 !text-xs"
        >
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ── Main UsersTab ──────────────────────────────────────────────────────────────
export default function UsersTab() {
  const currentUser = getCurrentUser();
  const isSysAdmin = isSystemAdmin();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const LIMIT = 20;

  const fetchUsers = useCallback(async (s = 0, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: s, limit: LIMIT });
      if (q) params.set('search', q);
      const res = await axios.get(`${API}/users?${params}`);
      setUsers(res.data.data || []);
      setTotal(res.data.total || 0);
      setSkip(s);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchUsers(0);
  }, [fetchUsers]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    fetchUsers(0, search);
  }

  async function handleDeactivate() {
    if (!deactivateTarget) return;
    try {
      await axios.delete(`${API}/users/${deactivateTarget.id}`);
      fetchUsers(skip);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to deactivate user');
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#1a1a2e' }}
          >
            User Management
          </h2>
          <p className="text-sm text-[#6b7280] mt-0.5">
            Manage team members and their access levels
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="!bg-[#1a1a2e] !text-white hover:!bg-[#2a2a4e] flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          New User
        </Button>
      </div>

      {/* Current user info banner */}
      {currentUser && (
        <div
          className="flex items-center gap-3 mb-4 px-4 py-3 rounded-lg border"
          style={{ background: '#f8f7f4', borderColor: '#e5e4df' }}
        >
          <ShieldAlert className="w-5 h-5" style={{ color: '#c9a84c' }} />
          <div className="text-sm">
            <span className="font-semibold" style={{ color: '#1a1a2e' }}>
              Logged in as {currentUser.full_name || currentUser.username}
            </span>
            <span className="text-[#6b7280]"> ({currentUser.role})</span>
          </div>
        </div>
      )}

      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by username..."
            className="!pl-9 !bg-white"
          />
        </div>
        <Button type="submit" variant="outline" className="!h-9">Search</Button>
        {search && (
          <Button
            type="button"
            variant="ghost"
            className="!h-9"
            onClick={() => { setSearch(''); fetchUsers(0, ''); }}
          >
            Clear
          </Button>
        )}
      </form>

      {/* Users table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#e5e4df' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#f8f7f4', borderBottom: '1px solid #e5e4df' }}>
              {['User', ...(isSysAdmin ? ['Company'] : []), 'Role', 'Status', 'Actions'].map(h => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                  style={{ color: '#6b7280' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-[#9ca3af]">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={isSysAdmin ? 5 : 4} className="px-4 py-12 text-center text-[#9ca3af]">
                  <UserX className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">No users found</p>
                  <p className="text-xs mt-1">Create your first team member above</p>
                </td>
              </tr>
            ) : (
              users.map((user, i) => {
                const isSelf = user.id === currentUser?.id;
                return (
                  <tr
                    key={user.id}
                    className="border-t"
                    style={{ borderColor: '#e5e4df', background: i % 2 === 0 ? '#ffffff' : '#f9f9f7' }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-semibold" style={{ color: '#1a1a2e' }}>
                          {user.full_name || user.username}
                          {isSelf && (
                            <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: '#fef3c7', color: '#92400e' }}>
                              You
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-[#9ca3af]">@{user.username}</span>
                        {user.email && (
                          <span className="text-xs text-[#9ca3af]">{user.email}</span>
                        )}
                      </div>
                    </td>
                    {isSysAdmin && (
                      <td className="px-4 py-3 text-xs text-[#6b7280]">
                        {user.company_id || '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <RoleBadge role={user.role} />
                    </td>
                    <td className="px-4 py-3">
                      <ActiveBadge active={user.active} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-1.5 rounded hover:bg-[#f3f4f6] transition-colors"
                          title="Edit user"
                        >
                          <Edit2 className="w-4 h-4 text-[#6b7280]" />
                        </button>
                        {!isSelf && user.active && (
                          <button
                            onClick={() => setDeactivateTarget(user)}
                            className="p-1.5 rounded hover:bg-red-50 transition-colors"
                            title="Deactivate user"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        skip={skip}
        limit={LIMIT}
        total={total}
        onPrev={() => fetchUsers(skip - LIMIT)}
        onNext={() => fetchUsers(skip + LIMIT)}
      />

      {/* Create/Edit dialog */}
      <UserFormDialog
        open={showCreate || !!editingUser}
        onClose={() => { setShowCreate(false); setEditingUser(null); }}
        onSaved={() => fetchUsers(editingUser ? skip : 0)}
        editingUser={editingUser}
      />

      {/* Deactivate confirm dialog */}
      <DeactivateConfirmDialog
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
        user={deactivateTarget}
      />
    </div>
  );
}
